import { z } from "zod";
import { CoachingAssessmentKind } from "../coaching-assessments.js";
import { ianaTimezoneSchema } from "../timezone.js";
export const CoachingSessionStatus = z.enum([
    "scheduled",
    "completed",
    "cancelled",
    "coachee_no_show",
    "coach_no_show",
]);
export const CoachingType = z.enum([
    "wellbeing",
    "executive",
    "conditions",
    "glp1",
    "anticoagulation",
]);
export const CareKind = z.enum(["coaching", "care_team"]);
/** Which side of a coaching relationship someone is on. */
export const CoachingActor = z.enum(["coachee", "coach"]);
export const CoachingSession = z.object({
    id: z.string(),
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime(),
    status: CoachingSessionStatus,
    goal: z.string().nullable(),
    topics: z.array(z.string()),
    coacheeId: z.string(),
    coacheeName: z.string(),
});
export const CoachingSlot = z.object({ start: z.string().datetime(), end: z.string().datetime() });
export const CoachingProgramme = z.object({
    coachingType: z.string(),
    /** coaching or care_team, from the programme type: it decides the words on both sides. */
    kind: CareKind,
    endsOn: z.string(),
    sessionCap: z.number().int(),
    sessionsRemaining: z.number().int(),
});
export const CoachingMessage = z.object({
    id: z.string(),
    fromMe: z.boolean(),
    body: z.string(),
    sentAt: z.string().datetime(),
    readAt: z.string().datetime().nullable(),
});
export const CoachingThreadResponse = z.object({ messages: z.array(CoachingMessage) });
export const SendCoachingMessageRequest = z.object({ body: z.string().trim().min(1).max(4000) });
const AssessmentAnswers = z.record(z.string(), z.union([z.number(), z.string(), z.array(z.string())]));
export const CoachingAssessment = z.object({
    id: z.string(),
    kind: CoachingAssessmentKind,
    sentAt: z.string().datetime(),
    completedAt: z.string().datetime().nullable(),
    /** When the coach opened the answers; null until then. */
    reviewedAt: z.string().datetime().nullable(),
    answers: AssessmentAnswers.nullable(),
});
export const CoachingAssessmentsResponse = z.object({ assessments: z.array(CoachingAssessment) });
export const SendCoachingAssessmentRequest = z.object({ kind: CoachingAssessmentKind });
/** Starts a questionnaire the member may open themselves, such as a daily check-in. */
export const StartCoachingAssessmentRequest = SendCoachingAssessmentRequest;
export const StartCoachingAssessmentResponse = z.object({ id: z.string() });
export const CompleteCoachingAssessmentRequest = z.object({ answers: AssessmentAnswers });
/** Whether the coachee shares what HealthOS sees with their coach. */
export const CoachingSharing = z.object({
    sharing: z.boolean(),
    since: z.string().datetime().nullable(),
});
/** What a coachee sees: their coach, their programme and their sessions. Null enrollment means not on a programme. */
export const CoachingMeResponse = z.object({
    programme: CoachingProgramme.nullable(),
    coach: z
        .object({
        name: z.string(),
        /** How they want members to see their role: "Coach", "Nurse", "Pharmacist". */
        title: z.string(),
        bio: z.string().nullable(),
        meetingUrl: z.string().nullable(),
        photoUrl: z.string().nullable(),
    })
        .nullable(),
    sessions: z.array(CoachingSession),
    unreadMessages: z.number().int(),
    assessments: z.array(CoachingAssessment),
    sharing: CoachingSharing,
});
export const CoachingSlotsResponse = z.object({ slots: z.array(CoachingSlot) });
export const BookCoachingSessionRequest = z.object({ startsAt: z.string().datetime() });
/** What the coach records once a session has happened. Booking, moving and cancelling have their own calls. */
export const CoachingOutcome = CoachingSessionStatus.exclude(["scheduled", "cancelled"]);
export const CoachingSessionPatch = z.object({
    status: CoachingOutcome.optional(),
    goal: z.string().max(500).nullable().optional(),
    topics: z.array(z.string().min(1).max(60)).max(12).optional(),
});
export const CoacheeRow = CoachingProgramme.extend({
    /** The member; a member on two programmes has a row for each. */
    id: z.string(),
    enrollmentId: z.string(),
    name: z.string(),
    nextSessionAt: z.string().datetime().nullable(),
    lastSessionAt: z.string().datetime().nullable(),
    awaitingOutcome: z.number().int(),
    coachId: z.string().nullable(),
    /** Messages from the coachee the coach has not opened yet. */
    unreadMessages: z.number().int(),
    companyId: z.string(),
    /** From Identity; null when Identity has no name for the company. */
    companyName: z.string().nullable(),
});
export const CoacheeSearchQuery = z.object({ q: z.string().trim().min(3).max(200) });
/** Coachees whose address matches, by id only: HealthOS never returns or keeps the address. */
export const CoacheeSearchResponse = z.object({ ids: z.array(z.string()) });
export const CoacheesResponse = z.object({ coachees: z.array(CoacheeRow) });
export const CoacheeProfileResponse = z.object({
    coachee: CoacheeRow,
    sessions: z.array(CoachingSession),
    /** What the coachee told onboarding they are working towards, in their words. */
    goal: z.string().nullable(),
});
export const CoachSessionsResponse = z.object({ sessions: z.array(CoachingSession) });
/** Coaching Ops: who is on the roster and how loaded they are. */
export const CoachRosterRow = z.object({
    id: z.string(),
    name: z.string(),
    photoUrl: z.string().nullable(),
    accepting: z.boolean(),
    timezone: z.string(),
    /** Coaching or care_team: a coach and a clinician are never interchangeable. */
    practice: CareKind,
    coachees: z.number().int(),
    upcomingSessions: z.number().int(),
    /** Sessions of theirs that have started and still need an outcome. */
    outcomesOwed: z.number().int(),
});
export const CoachingRosterResponse = z.object({ coaches: z.array(CoachRosterRow) });
export const CoacheeFilter = z.enum(["all", "no_coach", "unbooked", "ending", "messages"]);
export const CoacheeSort = z.enum(["name", "coach", "next", "last", "left", "ends"]);
/** The one company-filter choice for every company Identity has not named. */
export const UNNAMED_COMPANY = "unnamed";
export const OPS_COACHEES_PAGE_SIZE = 50;
/** Coaching Ops' coachee list, searched, filtered, sorted and paged on the server. */
/** Which side of Coaching Ops a read is for: coaching, or care teams. */
export const OpsKindQuery = z.object({ kind: CareKind.default("coaching") });
export const OpsCoacheesQuery = OpsKindQuery.extend({
    q: z.string().trim().max(200).optional(),
    company: z.string().max(64).optional(),
    coach: z.string().uuid().optional(),
    filter: CoacheeFilter.default("all"),
    sort: CoacheeSort.default("next"),
    dir: z.enum(["asc", "desc"]).default("asc"),
    page: z.coerce.number().int().min(0).max(10_000).default(0),
});
export const OpsCoacheesPage = z.object({
    coachees: z.array(CoacheeRow),
    /** How many matched everything asked, across all pages. */
    matched: z.number().int(),
    /** Everyone on a programme in this company and coach scope, before search and quick filter. */
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
    counts: z.record(CoacheeFilter, z.number().int()),
    companies: z.array(z.object({ id: z.string(), name: z.string().nullable(), count: z.number().int() })),
    /** Rows found only by a full email address, which the row does not show. */
    emailMatches: z.array(z.string()),
});
export const OpsCoachingOverview = z.object({
    onProgramme: z.number().int(),
    outcomesOwed: z.number().int(),
});
export const CoachingMemberSearchResponse = z.object({
    members: z.array(z.object({ id: z.string(), name: z.string(), kinds: z.array(CareKind) })),
});
/** The programmes a member has now, at most one of each kind, for their nav and Today. */
export const CoachingProgrammesResponse = z.object({
    programmes: z.array(z.object({
        kind: CareKind,
        coachingType: z.string(),
        professional: z.string().nullable(),
        unreadMessages: z.number().int(),
        waiting: z.number().int(),
        sessionsRemaining: z.number().int(),
        nextSessionAt: z.string().datetime().nullable(),
    })),
});
/** What a programme offers: its type, how many sessions, and when it ends. */
const ProgrammeTerms = z.object({
    coachingType: CoachingType,
    sessionCap: z.number().int().min(1).max(52),
    endsOn: z.string().date(),
});
/** Enrol a member, or change their coach or programme. Their company always comes from identity. */
export const CoachingEnrollmentRequest = ProgrammeTerms.extend({ coachUserId: z.string().uuid() });
export const EndCoachingEnrollmentResponse = z.object({
    ok: z.literal(true),
    cancelled: z.number().int(),
});
export const CoachingNotePhase = z.enum(["before", "after"]);
export const CoachingNoteVisibility = z.enum(["shared", "private"]);
/** A line on a session's agenda (before) or recap (after). Private notes reach only their author. */
export const CoachingNote = z.object({
    id: z.string(),
    sessionId: z.string(),
    phase: CoachingNotePhase,
    visibility: CoachingNoteVisibility,
    body: z.string(),
    mine: z.boolean(),
    author: CoachingActor,
    createdAt: z.string().datetime(),
});
export const CoachingNotesResponse = z.object({ notes: z.array(CoachingNote) });
export const AddCoachingNoteRequest = z.object({
    sessionId: z.string().uuid(),
    phase: CoachingNotePhase,
    visibility: CoachingNoteVisibility,
    body: z.string().trim().min(1).max(2000),
});
export const EditCoachingNoteRequest = AddCoachingNoteRequest.pick({ body: true });
export const CoachingActionCadence = z.enum(["once", "weekly", "daily"]);
export const CoachingActionStatus = z.enum(["open", "done", "dropped"]);
export const CoachingActionOutcome = z.enum(["acted", "dismissed"]);
/** Something agreed in a session, and how it has gone each time Heartbeat brought it up. */
export const CoachingAction = z.object({
    id: z.string(),
    sessionId: z.string().nullable(),
    title: z.string(),
    cadence: CoachingActionCadence,
    /** HH:MM in the member's time zone when it comes up; null is the usual morning. */
    localTime: z.string().nullable(),
    status: CoachingActionStatus,
    createdAt: z.string().datetime(),
    /** Times it came up so far, and how many of those the coachee did. */
    offered: z.number().int(),
    done: z.number().int(),
    /** The moment waiting for an answer now, if it is up today. */
    dueMomentId: z.string().nullable(),
    /** The last fortnight of times it came up, oldest first, with the answer if there was one. */
    history: z.array(z.object({ at: z.string().datetime(), outcome: CoachingActionOutcome.nullable() })),
});
export const CoachingActionsResponse = z.object({ actions: z.array(CoachingAction) });
const LOCAL_TIME = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use a time such as 18:00.");
export const AddCoachingActionRequest = z.object({
    sessionId: z.string().uuid().nullable(),
    title: z.string().trim().min(1).max(200),
    cadence: CoachingActionCadence,
    localTime: LOCAL_TIME.nullable().optional(),
});
export const CloseCoachingActionRequest = z.object({
    status: CoachingActionStatus.exclude(["open"]),
});
export const AnswerCoachingActionRequest = z.object({ outcome: CoachingActionOutcome });
export const SetCoachingSharingRequest = CoachingSharing.pick({ sharing: true });
/** One measure, this week against last, in the member's own terms. */
export const PreReadMetric = z.object({
    kind: z.string(),
    label: z.string(),
    unit: z.string(),
    better: z.enum(["higher", "lower"]).nullable(),
    current: z.number(),
    previous: z.number(),
    change: z.number(),
    /** Bigger than this member's own week-to-week variation. */
    notable: z.boolean(),
});
/** What a coach can read before a session, only while the coachee shares it. */
export const CoachPreReadResponse = z.object({
    sharing: CoachingSharing,
    metrics: z.array(PreReadMetric),
    /** Heartbeat moments over the last fortnight: offered, done, and set aside. */
    moments: z
        .object({ offered: z.number().int(), done: z.number().int(), notNow: z.number().int() })
        .nullable(),
});
/** "now" is owed today; "soon" can wait for the next session. */
const AttentionTone = z.enum(["now", "soon"]);
/** Something about one coachee that needs their coach, worked out from the record, never stored. */
export const CoachAttentionKind = z.enum([
    "flagged",
    "outcome",
    "messages",
    "answers",
    "agenda",
    "missed",
    "stalled",
    "ending",
    "unbooked",
]);
/** Where on the coachee's page the card opens. */
export const CoachAttentionSection = z.enum([
    "history",
    "messages",
    "questionnaires",
    "agenda",
    "actions",
    "book",
]);
export const CoachAttentionItem = z.object({
    id: z.string(),
    kind: CoachAttentionKind,
    coacheeId: z.string(),
    coacheeName: z.string(),
    title: z.string(),
    body: z.string(),
    tone: AttentionTone,
    section: CoachAttentionSection,
    at: z.string().datetime().nullable(),
});
export const CoachAttentionResponse = z.object({ items: z.array(CoachAttentionItem) });
/** Puts a card aside: until tomorrow, until next week, or for good because it is not needed. */
export const SetAttentionAsideRequest = z.object({
    until: z.enum(["tomorrow", "next_week", "for_good"]),
});
/** Something across the programme that needs Coaching Ops, worked out from the record, never stored. */
export const OpsAttentionKind = z.enum([
    "no_coach",
    "outcomes_owed",
    "ending",
    "unbooked",
    "paused",
]);
export const OpsAttentionItem = z.object({
    id: z.string(),
    kind: OpsAttentionKind,
    title: z.string(),
    body: z.string(),
    tone: AttentionTone,
    /** The coachee to open, when the item is about one person. */
    coacheeId: z.string().nullable(),
    /** The coach to open, when the item is about a coach. */
    coachId: z.string().nullable(),
});
export const OpsAttentionResponse = z.object({ items: z.array(OpsAttentionItem) });
/** A company's coaching programme, with how much of it is spoken for. */
export const CoachingCohort = z.object({
    id: z.string(),
    companyId: z.string(),
    companyName: z.string().nullable(),
    name: z.string(),
    coachingType: CoachingType,
    kind: CareKind,
    sessionCap: z.number().int(),
    endsOn: z.string(),
    seats: z.number().int(),
    coachId: z.string().nullable(),
    coachName: z.string().nullable(),
    joined: z.number().int(),
    open: z.number().int(),
    /** How many invites the cohort may hold in all, joined included: 120% of its seats. */
    ceiling: z.number().int(),
});
export const CoachingCohortsResponse = z.object({ cohorts: z.array(CoachingCohort) });
export const CreateCoachingCohortRequest = ProgrammeTerms.extend({
    companyId: z.string().uuid(),
    name: z.string().trim().min(1).max(120),
    seats: z.number().int().min(1).max(10_000),
    coachUserId: z.string().uuid().nullable(),
});
export const CreateCoachingCohortResponse = z.object({ ok: z.literal(true), id: z.string() });
/** Who runs a company programme from now on; null matches new members later. */
export const SetCohortCoachRequest = z.object({ coachUserId: z.string().uuid().nullable() });
/** How many of the programme's unmatched members the new coach picked up. */
export const SetCohortCoachResponse = z.object({ ok: z.literal(true), matched: z.number().int() });
export const CoachingInviteStatus = z.enum(["sent", "joined", "withdrawn"]);
export const CoachingInviteRow = z.object({
    id: z.string(),
    /** Null once they have joined or the invite was withdrawn: the address is not kept. */
    email: z.string().nullable(),
    status: CoachingInviteStatus,
    sentAt: z.string().datetime(),
    resentAt: z.string().datetime().nullable(),
    joinedAt: z.string().datetime().nullable(),
});
export const CoachingInvitesResponse = z.object({ invites: z.array(CoachingInviteRow) });
export const SendCoachingInvitesRequest = z.object({
    emails: z.array(z.string().trim().max(320)).min(1).max(500),
});
export const SendCoachingInvitesResponse = z.object({
    sent: z.number().int(),
    alreadyInvited: z.number().int(),
    failed: z.array(z.object({ email: z.string(), reason: z.string() })),
});
/** Whether Identity says a company has coaching; unknown when it did not answer. */
export const CompanyCoaching = z.enum(["on", "off", "unknown"]);
/** Which system runs a company's coaching: the legacy service until it is moved. */
export const CoachingSource = z.enum(["legacy", "healthos"]);
export const CoachingCompaniesResponse = z.object({
    companies: z.array(z.object({
        id: z.string(),
        name: z.string().nullable(),
        members: z.number().int(),
        coaching: CompanyCoaching,
        source: CoachingSource,
    })),
});
export const SetCoachingSourceRequest = z.object({ source: CoachingSource });
export const JoinCoachingRequest = z.object({ inviteId: z.string().uuid().nullable() });
export const JoinCoachingResponse = z.object({ joined: z.boolean() });
export const CoachWeekday = z.enum(["sun", "mon", "tue", "wed", "thu", "fri", "sat"]);
export const DayHours = z.object({
    start: z.string().regex(/^\d{2}:\d{2}$/),
    end: z.string().regex(/^\d{2}:\d{2}$/),
});
export const MeetingUrl = z
    .string()
    .url()
    .max(500)
    .refine((u) => u.startsWith("https://"), "Use an https meeting link.");
export const CoachSettings = z.object({
    bio: z.string().max(2000).nullable(),
    meetingUrl: MeetingUrl.nullable(),
    timezone: ianaTimezoneSchema.max(64),
    accepting: z.boolean(),
    weeklyHours: z.partialRecord(CoachWeekday, DayHours),
    timeOff: z.array(z.object({ from: z.string().datetime(), to: z.string().datetime() })),
    /** coaching, or care_team for a clinician. */
    practice: CareKind.default("coaching"),
    /** How members see their role, such as "Nurse" or "Pharmacist"; null uses the practice's default. */
    title: z.string().trim().min(1).max(40).nullable().default(null),
    /** Read only: where the coach's photo is served from. Ignored on save. */
    photoUrl: z.string().nullable().optional(),
    /** Read only: the coach's name from their HealthOS record. Ignored on save. */
    name: z.string().optional(),
});
/** A coach as Coaching Ops and the platform admin see them: who they are and every setting a coach can change. */
export const OpsCoachResponse = CoachSettings.extend({ id: z.string(), name: z.string() });
/** What Coaching Ops may set on a coach's behalf. The photo stays the coach's own. */
export const OpsCoachUpdate = CoachSettings.omit({ photoUrl: true, name: true });
export const SetCoachAcceptingRequest = CoachSettings.pick({ accepting: true });
/** A coach's unread messages across their coachees, for the nav badge. */
export const CoachUnreadResponse = z.object({ unread: z.number().int() });
//# sourceMappingURL=coaching.js.map